import { createSlice } from "../helper";
import * as ObjectExtensions from "../../utils/CoreExtensions/ObjectExtensions";
import * as ArrayExtensions from "../../utils/CoreExtensions/ArrayExtensions";
import upcomingMeetingWidgetReducers from "../reducers/groupOverviewTab/upcomingMeetingWidgetReducers";
import guidedConversationsWidgetReducers from "../reducers/groupOverviewTab/guidedConversationsWidgetReducers";

// Structure = {
//   data: {
//     customTerms: ,
//     wobMember: ,
//     isWorkingOnBehalf: ,
//     groupId: ,
//   },
//   taskIds: [],
//   tasks: {},
//   milestoneIds: [],
//   milestones: {
//     1: {
//       taskIds: [],
//       tasks: {

//       },
//       totalPages: ,
//       currentPage: ,
//       name: ,
//       description: ,
//     }
//   },
//   sectionIds: [],
//   sections: {
//     "completed": {
//       taskIds: [],
//       tasks: {

//       },
//       totalPages: ,
//       currentPage: ,
//       name: ,
//       description: ,
//     }
//   },
// }

const groupOverviewTabSlice = createSlice({
  name: 'groupOverviewTab',
  initialState: {
    data: {},
    tasksMetaData: {},
    tasksWidgetData: {
      showLoader: true,
      reloadTasksWidget: true
    },
    goalsWidgetData: {},
    goalsWidgetRefreshing: false,
    allTasks: {},
    upcomingMeetingWidget: {},
    guidedConversationsWidgetData: {
      reloadWidget: false,
      setWidgetLoader: false
    }
  },
  reducers: {
    setData: (state, action) => {
      state.data = action.payload;
    },
    setAllTasksData: (state, action) => {
      state.allTasks = ObjectExtensions.deepMerge(state.allTasks, action.payload);
    },
    setTasksWidgetData: (state, action) => {
      state.tasksWidgetData = action.payload;
    },
    updateTaskDetails: (state, action) => {
      state.allTasks[action.payload.taskId] = ObjectExtensions.deepMerge(state.allTasks[action.payload.taskId], action.payload.updatedTaskData);
      if(!action.payload.skipCategoryReload)
        updateAllCategoryTasksListingData(state, action.payload);
    },
    addNewTasks: (state, action) => {
      state.allTasks = ObjectExtensions.deepMerge(state.allTasks, action.payload.newTasks);
      updateAllCategoryTasksListingData(state, action.payload);
    },
    setTasksMetaData: (state, action) => {
      state.tasksMetaData = action.payload;
    },
    setGoalsWidgetData: (state, action) => {
      state.goalsWidgetData = ObjectExtensions.deepMerge(state.goalsWidgetData, action.payload);
    },
    setGoalsWidgetRefreshing: (state, action) => {
      state.goalsWidgetRefreshing = action.payload;
    },
    updateMilestoneData: (state, action) => {
      state.tasksWidgetData.milestonesData.milestones[action.payload.id] = ObjectExtensions.deepMerge(state.tasksWidgetData.milestonesData.milestones[action.payload.id], action.payload);
    },
    addNewMilestoneData: (state, action) => {
      state.tasksWidgetData.milestonesData.milestoneIds = [...state.tasksWidgetData.milestonesData.milestoneIds, action.payload.id];
      state.tasksWidgetData.milestonesData.milestones[action.payload.id] = {
        ...action.payload,
        scrollIntoView: true
      };

      if((state.tasksMetaData.newTaskData.milestoneOptions || []).length === 0){
        state.tasksMetaData.newTaskData.defaultSelectedMilestone = {id: action.payload.id, title: action.payload.title}
      }
      state.tasksMetaData.newTaskData.milestoneOptions = [...state.tasksMetaData.newTaskData.milestoneOptions, [action.payload.id, action.payload.title]]
    },
    deleteMilestone: (state, action) => {
      state.tasksWidgetData.milestonesData.milestoneIds = ArrayExtensions.remove(state.tasksWidgetData.milestonesData.milestoneIds, action.payload);
      state.tasksMetaData.newTaskData.milestoneOptions = state.tasksMetaData.newTaskData.milestoneOptions.filter((value) => value[0] !== action.payload);
    },
    updateMilestoneProgress: (state, action) => {
      state.tasksWidgetData.milestonesData.milestones[action.payload.milestoneId].completedFilteredTasks += (action.payload.taskCompleted ? 1 : -1);
    },
    updateGoalTasksProgress: (state, action) => {
      state.goalsWidgetData.goals[action.payload.goalId].completedGoalTasks += (action.payload.taskCompleted ? 1 : -1);
    },
    showTasksWidgetLoader: (state, action) => {
      state.tasksWidgetData.showLoader = action.payload;
    },
    reloadTasksWidget: (state, action) => {
      state.tasksWidgetData.reloadTasksWidget = action.payload;
    },
    deleteTask: (state, action) => {
      state.allTasks = ObjectExtensions.filter({...state.allTasks}, ([key, _value])=> key !== action.payload.taskId);
      updateAllCategoryTasksListingData(state, action.payload);
    },
    setGoalData: (state, action) => {
      state.goalsWidgetData.goals[action.payload.goalId] = ObjectExtensions.deepMerge(state.goalsWidgetData.goals[action.payload.goalId], action.payload.data)
    },
    deleteGoal: (state, action) => {
      if(state.goalsWidgetData.goals && state.goalsWidgetData.goals[action.payload.goalId])
        delete state.goalsWidgetData.goals[action.payload.goalId]
      state.goalsWidgetData.goalIds = state.goalsWidgetData.goalIds.filter(value => value !== action.payload.goalId)
    },
    ...upcomingMeetingWidgetReducers,
    ...guidedConversationsWidgetReducers
  }
});

const getUpdatedTaskIds = (allTaskIds, loadedTaskIds, newTaskIds, loadTasksByScroll) => {
  const size = loadedTaskIds?.length;
  if(!loadTasksByScroll && size === undefined) return loadedTaskIds;

  let updatedTaskIds = (loadedTaskIds || []).concat(newTaskIds);  // Merging new task ids to the category

  updatedTaskIds = allTaskIds.filter((taskId) => updatedTaskIds.includes(taskId));  // Getting the only ids present in both allTaskIds and updatedTaskIds

  updatedTaskIds.sort((id1, id2) => allTaskIds.indexOf(id1) - allTaskIds.indexOf(id2)); // Updating the sequence of task ids to follow the allTaskIds

  return updatedTaskIds;
}

const updateCategoryData = (categoryListDataState, newCategoryData, newTaskIds) => {
  const categoryData = categoryListDataState[newCategoryData.id];
  const updatedTaskIds = getUpdatedTaskIds(newCategoryData.allTaskIds, categoryData.taskIds, newTaskIds, newCategoryData.loadTasksByScroll);
  categoryListDataState[newCategoryData.id] = ObjectExtensions.deepMerge(categoryListDataState[newCategoryData.id],
  {...newCategoryData,
    taskIds: updatedTaskIds,
    hasMoreTasks: !updatedTaskIds || !newCategoryData.allTaskIds.every(taskId => updatedTaskIds.includes(taskId))
  });
}

const updateAllCategoryTasksListingData = (state, payload) => {
  if(payload.isSectionTask){
    updateSectionsCategoryData(state, payload);
  }
  else if(payload.isMilestoneTask){
    updateMilestonesCategoryData(state, payload);
  }
  else {
    state.tasksWidgetData.taskIds = payload.allTaskIds;
  }

  if(payload.isGoalTask){
    updateGoalsCategoryData(state, payload);
  }
}

const updateSectionsCategoryData = (state, payload) => {
  updateCategoryData(state.tasksWidgetData.sectionsData.sections, payload.updatedSectionData, payload.newTaskIds);
  if(payload.updatedSectionData.id !== payload.prevSectionData.id){
    updateCategoryData(state.tasksWidgetData.sectionsData.sections, payload.prevSectionData, payload.newTaskIds);
  }
}

const updateMilestonesCategoryData = (state, payload) => {
  updateCategoryData(state.tasksWidgetData.milestonesData.milestones, payload.updatedMilestoneData, payload.newTaskIds);
  if(payload.updatedMilestoneData.id !== payload.prevMilestoneData.id){
    updateCategoryData(state.tasksWidgetData.milestonesData.milestones, payload.prevMilestoneData, payload.newTaskIds);
  }
}

const updateGoalsCategoryData = (state, payload) => {
  updateCategoryData(state.goalsWidgetData.goals, payload.updatedGoalData, payload.newTaskIds);
  if(payload.updatedGoalData.id !== payload.prevGoalData.id){
    updateCategoryData(state.goalsWidgetData.goals, payload.prevGoalData, payload.newTaskIds);
  }
}

export const groupOverviewTabActions =  groupOverviewTabSlice.actions;

export default groupOverviewTabSlice;
